/**
 * Spotlight — the hover surface for cards (web, fine pointers only):
 *   - a soft indigo glow that follows the pointer across the surface,
 *   - a rim light: the border lights up green → violet nearest the pointer,
 *   - a lift (`lift`, default) or a 3D tilt toward the pointer (`tilt`).
 * The pointer position goes into CSS variables, so there's no React render
 * per move. Native, touch and reduced motion: a plain View.
 *
 *   <Spotlight style={styles.card}>...</Spotlight>
 *   <Spotlight tilt>...</Spotlight>
 *   <Spotlight lift={false} glow="rgba(52,211,153,0.10)" rim={false}>...</Spotlight>
 */
import React, { forwardRef, useEffect, useRef } from 'react';
import { View } from 'react-native';
import { IS_WEB, useReducedMotion } from '../../utils/motion';
import injectCss from '../../utils/injectCss';

const CSS = `
[data-spotlight] { position: relative; transition: transform 700ms cubic-bezier(0.16,1,0.3,1), box-shadow 480ms cubic-bezier(0.2,0,0,1), border-color 240ms cubic-bezier(0.2,0,0,1); }
[data-spotlight]::after { content: ''; position: absolute; inset: 0; border-radius: inherit; pointer-events: none; opacity: 0; transition: opacity 360ms cubic-bezier(0.2,0,0,1);
  background: radial-gradient(520px circle at var(--bo-mx, 50%) var(--bo-my, 0%), var(--bo-glow, rgba(139,92,246,0.24)), transparent 50%); }
[data-spotlight-rim] { position: absolute; inset: 0; border-radius: inherit; padding: 1px; pointer-events: none; opacity: 0; transition: opacity 360ms cubic-bezier(0.2,0,0,1);
  background: radial-gradient(260px circle at var(--bo-mx, 50%) var(--bo-my, 0%), rgba(52,211,153,0.95), rgba(139,92,246,0.55) 38%, transparent 72%);
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); -webkit-mask-composite: xor; mask-composite: exclude; }
/* Keeps the hover area under the pointer while the card lifts (no edge jitter). */
[data-spotlight="lift"]::before, [data-spotlight="tilt"]::before { content: ''; position: absolute; left: 0; right: 0; bottom: -10px; height: 10px; }
@media (hover: hover) and (pointer: fine) {
  [data-spotlight]:hover::after, [data-spotlight]:hover > [data-spotlight-rim] { opacity: 1; }
  [data-spotlight="lift"]:hover { transform: translate3d(0, -8px, 0); box-shadow: 0 34px 80px -30px rgba(99,102,241,0.6); }
  [data-spotlight="tilt"]:hover { transform: perspective(1100px) rotateX(var(--bo-rx, 0deg)) rotateY(var(--bo-ry, 0deg)) translate3d(0, -8px, 0);
    box-shadow: 0 40px 90px -34px rgba(99,102,241,0.65); transition-duration: 180ms, 480ms, 240ms; }
}
@media (prefers-reduced-motion: reduce) { [data-spotlight] { transition: none; } [data-spotlight="lift"]:hover, [data-spotlight="tilt"]:hover { transform: none; } }
`;

const MAX_TILT = 5; // degrees
const RIM = { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' };

const Spotlight = forwardRef(function Spotlight({ children, style, lift = true, tilt = false, rim = true, glow, dataSet, ...rest }, outerRef) {
    const ref = useRef(null);
    const reduced = useReducedMotion();
    const on = IS_WEB && !reduced;
    const mode = tilt ? 'tilt' : lift ? 'lift' : 'flat';

    useEffect(() => {
        if (!on) return undefined;
        injectCss('spotlight', CSS);
        const node = ref.current;
        if (!node?.addEventListener) return undefined;
        if (glow) node.style.setProperty('--bo-glow', glow);
        const move = (e) => {
            const r = node.getBoundingClientRect();
            const x = e.clientX - r.left;
            const y = e.clientY - r.top;
            node.style.setProperty('--bo-mx', `${x}px`);
            node.style.setProperty('--bo-my', `${y}px`);
            if (tilt && r.width && r.height) {
                node.style.setProperty('--bo-ry', `${((x / r.width) - 0.5) * 2 * MAX_TILT}deg`);
                node.style.setProperty('--bo-rx', `${(0.5 - (y / r.height)) * 2 * MAX_TILT}deg`);
            }
        };
        // Next hover starts level instead of at the last tilt.
        const leave = () => { node.style.setProperty('--bo-rx', '0deg'); node.style.setProperty('--bo-ry', '0deg'); };
        node.addEventListener('pointermove', move, { passive: true });
        node.addEventListener('pointerleave', leave);
        return () => { node.removeEventListener('pointermove', move); node.removeEventListener('pointerleave', leave); };
    }, [on, glow, tilt]);

    const setRefs = (n) => {
        ref.current = n;
        if (typeof outerRef === 'function') outerRef(n);
        else if (outerRef) outerRef.current = n;
    };

    return (
        <View ref={setRefs} style={style} dataSet={on ? { ...dataSet, spotlight: mode } : dataSet} {...rest}>
            {children}
            {on && rim && <View style={RIM} dataSet={{ spotlightRim: '' }} aria-hidden accessibilityElementsHidden importantForAccessibility="no-hide-descendants" />}
        </View>
    );
});

export default Spotlight;
